import moment from 'moment'
import { useState } from 'react'
import {
  Alert,
  Button,
  FloatingLabel,
  Form,
  Modal,
  Spinner,
} from 'react-bootstrap'
import { useForm } from 'react-hook-form'
import { useParams, useSearchParams } from 'react-router-dom'
import { useInsertConsultationSlotsMutation } from '../generated/urql.administrator'
import toastsService from '../lib/toastsService'

type FormConsultationSlotGenerator = {
  day: string
  timeFrom: string
  timeTo: string
  durationMinutes: string
}

type ConsultationSlotGeneratorModalProps = {
  isVisible: boolean
  onClose: () => void
}

const ConsultationSlotGeneratorModal = ({
  isVisible,
  onClose,
}: ConsultationSlotGeneratorModalProps) => {
  const { id } = useParams()
  const consultationId = id ? Number(id) : undefined
  const {
    handleSubmit,
    formState: { errors },
    register,
  } = useForm<FormConsultationSlotGenerator>()
  const [{ fetching: isInserting }, insertConsultationSlots] =
    useInsertConsultationSlotsMutation()

  const onGenerateSlots = async (values: FormConsultationSlotGenerator) => {
    if (!consultationId) {
      return
    }
    const generatedSlots = generateSlotsForDay(values)
    const objects = generatedSlots.map((s) => ({ ...s, consultationId }))
    const { error } = await insertConsultationSlots({ objects })

    if (error) {
      const code = error.graphQLErrors.find(
        (e) => e.extensions.code === 'constraint-violation'
      )
      const message = code
        ? 'Zablokowano aby zapobiec duplikatom terminów'
        : error.message || 'Coś poszło nie tak'
      toastsService.add({
        title: 'Błąd!',
        message,
        variant: 'danger',
      })
    } else {
      toastsService.add({
        title: 'Sukces!',
        message: 'Terminy zostały wygenerowanie poprawnie',
        variant: 'success',
      })
      onClose()
    }
  }

  return (
    <Modal show={isVisible} onHide={onClose} size="lg">
      <Form onSubmit={handleSubmit(onGenerateSlots)}>
        <Modal.Header closeButton>
          <Modal.Title>Generator terminów</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <FloatingLabel label="Dzień" className="mb-3">
            <Form.Control
              isInvalid={!!errors.day}
              type="date"
              {...register('day', {
                required: 'Proszę wybrać dzień',
              })}
            />
            {errors.day && (
              <Form.Control.Feedback>
                {errors.day.message}
              </Form.Control.Feedback>
            )}
          </FloatingLabel>

          <FloatingLabel label="Czas od" className="mb-3">
            <Form.Control
              isInvalid={!!errors.timeFrom}
              type="time"
              {...register('timeFrom', {
                required: 'Proszę wybrać godzinę',
              })}
            />
            {errors.timeFrom && (
              <Form.Control.Feedback>
                {errors.timeFrom.message}
              </Form.Control.Feedback>
            )}
          </FloatingLabel>

          <FloatingLabel label="Czas do" className="mb-3">
            <Form.Control
              isInvalid={!!errors.timeTo}
              type="time"
              {...register('timeTo', {
                required: 'Proszę wybrać godzinę',
              })}
            />
            {errors.timeTo && (
              <Form.Control.Feedback>
                {errors.timeTo.message}
              </Form.Control.Feedback>
            )}
          </FloatingLabel>

          <FloatingLabel label="Długość pojedynczego terminu" className="mb-3">
            <Form.Select
              {...register('durationMinutes', {
                required: 'Proszę opcję',
              })}
              defaultValue={30}
            >
              <option value={15}>15 min</option>
              <option value={30}>30 min</option>
              <option value={45}>45 min</option>
              <option value={60}>1 h</option>
            </Form.Select>
          </FloatingLabel>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="success"
            type="submit"
            disabled={isInserting}
            className="d-flex gap-2 align-items-center justify-content-center"
            style={{ width: 110 }}
          >
            {isInserting && (
              <Spinner size="sm" animation="border" role="status" />
            )}
            Generuj
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}

export default ConsultationSlotGeneratorModal

type Slot = { dateFrom: string; dateTo: string }

const generateSlotsForDay = (values: FormConsultationSlotGenerator): Slot[] => {
  const { day, timeFrom, timeTo, durationMinutes } = values

  let start = moment(`${day} ${timeFrom}`, 'YYYY-MM-DD HH:mm', true)
  const endWindow = moment(`${day} ${timeTo}`, 'YYYY-MM-DD HH:mm', true)

  const out: Slot[] = []
  while (start.isBefore(endWindow)) {
    const next = start.clone().add(Number(durationMinutes), 'minutes')
    if (next.isAfter(endWindow)) break

    out.push({
      dateFrom: start.toDate().toISOString(),
      dateTo: next.toDate().toISOString(),
    })

    start = next
  }
  return out
}
